/**
 * Shared stage-feedback capability contracts.
 *
 * A room with `supportsStageFeedback: true` can let participants target
 * playful feedback at normalized coordinates on a stage or media surface.
 */

export const STAGE_FEEDBACK_KINDS = ['tomato', 'rose', 'egg'] as const;

export type StageFeedbackKind = typeof STAGE_FEEDBACK_KINDS[number];

/** Exactly one armed tool owns stage pointer input. */
export type StageTool =
    | { type: 'throw'; kind: StageFeedbackKind }
    | { type: 'pen'; color: 'yellow' | 'red' }
    | null;

export const STAGE_FEEDBACK_SURFACES = [
    'stage',
    'video',
    'broadcast',
    'shared_stage_media',
] as const;

export type StageFeedbackSurface = typeof STAGE_FEEDBACK_SURFACES[number];

export interface StageFeedbackTarget {
    /** Horizontal coordinate normalized to the receiver surface, 0..1. */
    x: number;
    /** Vertical coordinate normalized to the receiver surface, 0..1. */
    y: number;
    surface?: StageFeedbackSurface;
}

export interface StageFeedbackActionPayload {
    kind: StageFeedbackKind;
    target: StageFeedbackTarget;
    clientEventId?: string;
}

export const isStageFeedbackKind = (
    value: unknown,
): value is StageFeedbackKind => (
    typeof value === 'string' &&
    (STAGE_FEEDBACK_KINDS as readonly string[]).includes(value)
);

export const isStageFeedbackSurface = (
    value: unknown,
): value is StageFeedbackSurface => (
    typeof value === 'string' &&
    (STAGE_FEEDBACK_SURFACES as readonly string[]).includes(value)
);
